import { BuildingType, ZoneType } from '../../../types';
import type { CityStats, Grid, SimulationBreakdown } from '../../../types';
import { GRID_SIZE, BUILDINGS, PRODUCTS, INTEREST_RATE_PER_LOAN } from '../../../constants';
import { checkZoneViolations } from './complianceRules';
import type { TickResult } from '../types/tickResult';

/** Compute budget, compliance, and threat deltas for one tick */
export function calculateTickMetrics(grid: Grid, stats: CityStats, tickRef: number): TickResult {
  let incomeBuildings = 0;
  let incomeProducts = 0;
  let expenseMaintenance = 0;
  let compGenerated = 0;
  let threatGenerated = 0;
  let threatMitigated = 0;

  let dailyBudget = 0;
  let dailyComp = 0;
  let dailyThreat = 0;
  const buildingCounts: Record<string, number> = {};
  let revenueBuildingsCount = 0;
  let revenueHalted = false;

  const { violations, threatPenalty, violationTiles } = checkZoneViolations(grid);
  dailyThreat += threatPenalty;
  threatGenerated += threatPenalty;

  const hasLoadBalancer = grid.flat().some((t) => t.buildingType === BuildingType.LoadBalancer && t.isConnected);
  const hasVault = grid.flat().some((t) => t.buildingType === BuildingType.Vault && t.isConnected);

  const directions = [
    [0, 1],
    [0, -1],
    [1, 0],
    [-1, 0],
  ];

  grid.flat().forEach((tile) => {
    if (tile.buildingType !== BuildingType.None) {
      buildingCounts[tile.buildingType] = (buildingCounts[tile.buildingType] || 0) + 1;

      if (tile.buildingType !== BuildingType.Pipeline && tile.buildingType !== BuildingType.Road) {
        if (tile.isConnected) {
          const config = BUILDINGS[tile.buildingType];
          const level = tile.level || 1;
          const multiplier = 1 + (level - 1) * 0.15;

          let bGen = Math.floor(config.budgetGen * multiplier);
          const cGen = Math.floor(config.complianceGen * multiplier);
          const tGen = Math.floor(config.threatGen * multiplier);

          if (config.zone === ZoneType.Public && !hasLoadBalancer && bGen > 0) {
            bGen = Math.floor(bGen * 0.3);
          }

          if (config.zone === ZoneType.Ops || config.zone === ZoneType.Public) {
            let hasRoad = false;
            for (const [dx, dy] of directions) {
              const nx = tile.x + dx;
              const ny = tile.y + dy;
              if (nx >= 0 && nx < GRID_SIZE && ny >= 0 && ny < GRID_SIZE) {
                if (grid[ny][nx].buildingType === BuildingType.Road) {
                  hasRoad = true;
                  break;
                }
              }
            }
            if (hasRoad && bGen > 0) {
              bGen = Math.floor(bGen * 1.2);
            }
          }

          if (bGen > 0) incomeBuildings += bGen;
          if (bGen < 0) expenseMaintenance += Math.abs(bGen);

          if (cGen > 0) compGenerated += cGen;
          if (tGen > 0) threatGenerated += tGen;
          if (tGen < 0) threatMitigated += Math.abs(tGen);

          dailyBudget += bGen;
          dailyComp += cGen;
          dailyThreat += tGen;

          if (config.budgetGen > 0) revenueBuildingsCount++;
        }
      }
    }
  });

  const internetCount = buildingCounts[BuildingType.Internet] || 0;
  const internetMultiplier = 1 + internetCount * 0.2;
  const activeProdCount = stats.activeProducts.length;

  let productRiskTotal = 0;

  stats.activeProducts.forEach((prodId) => {
    const prod = PRODUCTS.find((p) => p.id === prodId);
    if (prod) {
      let revenue = prod.revenue;
      if ((prodId === 'prod_crypto' || prodId === 'prod_savings') && !hasVault) {
        revenue = 0;
        if (tickRef % 5 === 0) revenueHalted = true;
      } else if (prodId === 'prod_crypto' || prodId === 'prod_savings') {
        revenue = Math.floor(revenue * internetMultiplier);
      }

      incomeProducts += revenue;
      dailyBudget += revenue;

      const scalingRisk = prod.risk + activeProdCount * 0.2;
      productRiskTotal += scalingRisk;
      dailyComp -= scalingRisk;
    }
  });

  const hubCount = buildingCounts[BuildingType.Core] || 0;
  const coreDrain = Math.floor(1 + hubCount * 1 + revenueBuildingsCount * 0.3);
  const interestDrain = stats.loanCount * INTEREST_RATE_PER_LOAN;

  expenseMaintenance += coreDrain;

  let decay = 0;
  if (stats.compliance > 80) decay = 0.5;

  const netBudget = dailyBudget - interestDrain;
  const netComp = dailyComp - coreDrain - decay;

  const naturalThreatDecay = Math.max(0.5, Math.floor(stats.threat * 0.02));
  dailyThreat -= naturalThreatDecay;

  const breakdown: SimulationBreakdown = {
    income: {
      buildings: incomeBuildings,
      products: incomeProducts,
      total: incomeBuildings + incomeProducts,
    },
    expenses: {
      maintenance: expenseMaintenance,
      interest: interestDrain,
      total: expenseMaintenance + interestDrain,
    },
    compliance: {
      generated: compGenerated,
      decay: coreDrain + decay,
      productRisk: productRiskTotal,
      net: netComp,
    },
    threat: {
      generated: threatGenerated,
      mitigated: threatMitigated,
      net: dailyThreat,
    },
  };

  return {
    netBudget,
    netComp,
    dailyThreat,
    deltas: { budget: netBudget, compliance: netComp, threat: dailyThreat },
    buildingCounts,
    revenueHalted,
    violations,
    violationTiles,
    breakdown,
  };
}